/**
 * Slash command routing for global LexCounsel — maps to existing orchestration skills.
 */

export type LexCounselSlashCommand =
  | 'draft'
  | 'review'
  | 'research'
  | 'hearing'
  | 'strategy'
  | 'summarize'
  | 'timeline'
  | 'check'
  | 'improve'
  | 'compare'
  | 'explain'
  | 'cite'
  | 'translate';

export interface ParsedSlashInput {
  command: LexCounselSlashCommand | null;
  args: string;
  raw: string;
}

const COMMAND_SET = new Set<string>([
  'draft',
  'review',
  'research',
  'hearing',
  'strategy',
  'summarize',
  'timeline',
  'check',
  'improve',
  'compare',
  'explain',
  'cite',
  'translate',
]);

export function parseSlashCommand(input: string): ParsedSlashInput {
  const trimmed = input.trim();
  if (!trimmed.startsWith('/')) {
    return { command: null, args: trimmed, raw: trimmed };
  }
  const withoutSlash = trimmed.slice(1);
  const spaceIdx = withoutSlash.indexOf(' ');
  const cmd = (spaceIdx === -1 ? withoutSlash : withoutSlash.slice(0, spaceIdx)).toLowerCase();
  const args = spaceIdx === -1 ? '' : withoutSlash.slice(spaceIdx + 1).trim();
  if (COMMAND_SET.has(cmd)) {
    return { command: cmd as LexCounselSlashCommand, args, raw: trimmed };
  }
  return { command: null, args: trimmed, raw: trimmed };
}

export function orchestrationIntentForCommand(command: LexCounselSlashCommand): string {
  switch (command) {
    case 'draft':
    case 'improve':
      return 'draft_document';
    case 'research':
    case 'cite':
      return 'lex_counsel';
    default:
      return 'case_qa';
  }
}

export function commandHelpText(): string {
  return [
    '/draft — drafting assistance',
    '/review — document review & filing readiness',
    '/research — legal research memorandum',
    '/hearing — hearing preparation briefing',
    '/strategy — strategic case analysis',
    '/summarize — summarize case or proceedings',
    '/timeline — case chronology',
    '/check — readiness & compliance check',
    '/explain — explain last answer',
    '/cite — find supporting authorities',
  ].join('\n');
}

const PARA_REF_RE =
  /\b(?:para(?:graph)?|clause|section|point)\s*(?:no\.?|number)?\s*(\d+)\b/i;

/** Detect paragraph references like "para 3" or "clause 5" in user text. */
export function parseParagraphReference(text: string): number | null {
  const match = text.match(PARA_REF_RE);
  if (!match) return null;
  const index = Number(match[1]);
  if (!Number.isFinite(index) || index < 1) return null;
  return index;
}
